import { type PointerEvent, useCallback, useRef, useState } from "react";

const ACTIVATE = 12;
const SNAP = 40;

type UseSidebarDragOptions = {
	enabled: boolean;
	compact: boolean;
	onCollapse: () => void;
	onExpand: () => void;
};

/**
 * Drag-to-collapse on the chevron only. Never capture the pointer until the
 * gesture is clearly a drag, otherwise menu clicks never fire.
 */
export function useSidebarDrag({
	enabled,
	compact,
	onCollapse,
	onExpand,
}: UseSidebarDragOptions) {
	const [offset, setOffset] = useState(0);
	const startX = useRef<number | null>(null);
	const dragging = useRef(false);
	const skipClick = useRef(false);
	const target = useRef<HTMLElement | null>(null);
	const pointerId = useRef<number | null>(null);

	const release = useCallback((node: HTMLElement | null) => {
		if (
			node &&
			pointerId.current != null &&
			node.hasPointerCapture(pointerId.current)
		) {
			node.releasePointerCapture(pointerId.current);
		}
		pointerId.current = null;
		target.current = null;
		startX.current = null;
		dragging.current = false;
		setOffset(0);
	}, []);

	const onPointerDown = useCallback(
		(event: PointerEvent<HTMLElement>) => {
			if (!enabled || event.button !== 0) {
				return;
			}
			skipClick.current = false;
			startX.current = event.clientX;
			dragging.current = false;
			target.current = event.currentTarget;
			pointerId.current = event.pointerId;
		},
		[enabled],
	);

	const onPointerMove = useCallback(
		(event: PointerEvent<HTMLElement>) => {
			if (startX.current == null) {
				return;
			}
			const dx = event.clientX - startX.current;
			if (!dragging.current) {
				if (Math.abs(dx) < ACTIVATE) {
					return;
				}
				dragging.current = true;
				skipClick.current = true;
				event.currentTarget.setPointerCapture(event.pointerId);
			}
			setOffset(compact ? Math.max(0, dx) : Math.min(0, dx));
		},
		[compact],
	);

	const onPointerUp = useCallback(
		(event: PointerEvent<HTMLElement>) => {
			if (dragging.current && startX.current != null) {
				const dx = event.clientX - startX.current;
				if (!compact && dx <= -SNAP) {
					onCollapse();
				} else if (compact && dx >= SNAP) {
					onExpand();
				}
			}
			release(event.currentTarget);
		},
		[compact, onCollapse, onExpand, release],
	);

	const onClickCapture = useCallback(
		(event: { preventDefault(): void; stopPropagation(): void }) => {
			if (!skipClick.current) {
				return;
			}
			skipClick.current = false;
			event.preventDefault();
			event.stopPropagation();
		},
		[],
	);

	return {
		offset,
		bind: {
			onPointerDown,
			onPointerMove,
			onPointerUp,
			onPointerCancel: onPointerUp,
			onLostPointerCapture: () => release(target.current),
			onClickCapture,
		},
	};
}
